import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

import { useDeleteDraft } from 'dataQueries/purchaseFlowDrafts.query'
import { pfCatalogueQueryKey } from 'dataQueries/purchaseFlowRevamp.query'
import { useLoadDraftIntoStore } from 'components/pages/PurchaseFlowRevamp/store/rehydrate'
import { Path } from 'constants/router'
import { usePfEntryNavigation } from 'components/pages/PurchaseFlowRevamp/entry/usePfEntryNavigation'
import { useRevalidateAdminTarget } from 'components/pages/PurchaseFlowRevamp/entry/useAdminTargetRevalidation'
import { PfRevampEventName, usePfRevampAnalytics } from 'components/pages/PurchaseFlowRevamp/analytics'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { PropertyEndpoint, RoleMimetype } from 'constants/API'
import { useAPI } from 'utils/API'
import { logger } from 'utils/logging'
import type { DraftMeta } from 'utils/purchaseFlowDrafts'
import type { ResolvedCatalogue } from 'models/purchaseFlowRevamp'

import { usePfStore } from '../store/store'
import { catalogueCountry } from '../store/slices/propertySlice'
import { useUserData } from 'components/contexts/UserDataContext'
import { resolveResumePath } from './resumePath'
import { placedSessionOrder } from './placedSessions'

/**
 * Loads a saved draft into the store and returns where the flow should open, or null when it cannot be resumed.
 *
 * Recovery on resume:
 *   1. Admin target revalidated first; an unconfirmed client sends the admin back to client selection.
 *   2. Deleted property (admin scope): a 404 deletes the draft and reports it.
 *   3. Stale catalogue items are dropped against a cached catalogue, with a count toast.
 *   4. Uploads that expired, and staging rooms whose photos went with them, are reported.
 */
export const useResumeDraft = () => {
  const { t, i18n } = useTranslation(['purchase_flow_revamp'])
  const { resolveEntryPath } = usePfEntryNavigation()
  const { spawnErrorToast, spawnWarningToast } = useSnackbar()
  const deleteDraft = useDeleteDraft()
  const loadDraftIntoStore = useLoadDraftIntoStore()
  const revalidateAdminTarget = useRevalidateAdminTarget()
  const trackAnalytics = usePfRevampAnalytics()
  const queryClient = useQueryClient()
  const { baseUserData } = useUserData()
  const api = useAPI<PropertyEndpoint>()

  return useCallback(async (draft: DraftMeta): Promise<string | null> => {
    // Its order was placed here and only the draft's delete failed: the order, not the cart, is what is left.
    const placed = draft.sessionId ? placedSessionOrder(draft.sessionId) : null
    if (placed) {
      logger.info('pf-revamp resume refused: the draft\'s order was already placed', undefined, {
        context: { component: 'useResumeDraft', action: 'resumePlacedDraft', metadata: { draftId: draft.draftId, orderId: placed.orderId } },
      })
      deleteDraft.mutate(draft.draftId)
      spawnWarningToast(t('purchase_flow_revamp:session_restore.ordered_text', { orderId: placed.orderId }))
      return null
    }

    const result = await loadDraftIntoStore(draft.draftId)

    if (!result.ok) {
      if (result.reason === 'version-mismatch') {
        spawnErrorToast(t('purchase_flow_revamp:drafts.resume_version_mismatch'))
      } else {
        spawnErrorToast(t('purchase_flow_revamp:drafts.resume_error'))
      }
      return null
    }

    // ── Post-hydrate recovery ────────────────────────────────────────────────

    // Read freshly-hydrated store state.
    const { property, items, targetWorkspaceId } = usePfStore.getState()
    const propertyId = property?.id
    const workspaceId = targetWorkspaceId

    // 1. Admin target. Runs FIRST, and before the property check below, because
    //    that check reads the property THROUGH the stored workspace and deletes
    //    the draft on a 404: a merely stale workspace pointer would take a
    //    perfectly good cart and property down with it. A draft holds ids and can
    //    be two weeks old, so whether the client is still in that workspace is a
    //    live question — confirmed against the same set the picker offers. The
    //    lookup also restores the contact + entitlement the gate had resolved and
    //    the draft never carried.
    if (await revalidateAdminTarget() === 'needs-review') {
      spawnWarningToast(t('purchase_flow_revamp:drafts.resume_confirm_client'))
      trackAnalytics({
        type: PfRevampEventName.DRAFT_RESUMED,
        payload: { draftId: draft.draftId, ageSeconds: -1, lastStep: result.lastStep, landedOn: Path.PURCHASE_FLOW_REVAMP_SELECT_PROPERTY },
      })
      // The gate, not the catalogue: the email was dropped, so the drawer opens
      // on client selection. The property check is deliberately skipped — it
      // would run against the unconfirmed workspace and could delete the draft.
      return Path.PURCHASE_FLOW_REVAMP_SELECT_PROPERTY
    }

    // 2. Deleted-property check (admin scope only — client drafts have no workspaceId).
    if (propertyId && workspaceId) {
      try {
        // Client representation pinned: this check only ever runs in admin scope
        // (the draft carries a target workspace), and the workspace-scoped
        // property endpoints don't serve the admin mimetype — a 406 would fall
        // into the non-404 branch below and silently disable the check.
        await api.get(
          PropertyEndpoint.GET_PROPERTY_DETAIL,
          { workspaceId, propertyId },
          false,
          { headers: { Accept: RoleMimetype.CLIENT } }
        )
      }
      catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          logger.warn('Draft property no longer exists — deleting draft', {
            context: {
              component: 'ContinueOrderDialog',
              action: 'handleResumeDraft',
              metadata: { draftId: draft.draftId, propertyId },
            },
          })
          deleteDraft.mutate(draft.draftId)
          // Clear stale hydration so the user doesn't see ghost
          // property / cart data from a draft whose property no longer exists.
          usePfStore.getState().resetPersistedState()
          spawnErrorToast(t('purchase_flow_revamp:drafts.resume_property_missing'))
          return null
        }
        // Non-404 errors (network, server 5xx) — log and continue rather than
        // blocking resume. The user will encounter the error later if the
        // property is truly unavailable.
        logger.error('Property detail check failed during draft resume', err, {
          context: {
            component: 'ContinueOrderDialog',
            action: 'handleResumeDraft',
            metadata: { draftId: draft.draftId, propertyId },
          },
        })
      }
    }

    // 3. Stale catalogue-item check (cache-read-only — no new fetch triggered).
    //    Reads a pre-existing catalogue entry if one is cached for the property's
    //    countryCode. Skips silently if the cache doesn't have one yet.
    //
    //    D3: this used to hand-roll the key WITHOUT the `ws`/`sessionId`
    //    segments `useCatalogue` writes, so the lookup was a guaranteed miss and
    //    `clearStaleItems` never ran. We now build the key with the hook's own
    //    `pfCatalogueQueryKey` and match PARTIALLY (`getQueriesData`) — at resume
    //    time we can't know which ws/session cached the catalogue, and any of
    //    them answers "does this shell id still exist?".
    // Keyed exactly as usePfCatalogue keys it, so a property with an unknown country is checked too.
    if (items.length > 0 && property) {
      const locale = property.locale ?? i18n.language
      const cachedCatalogue = queryClient
        .getQueriesData<ResolvedCatalogue>({
          queryKey: pfCatalogueQueryKey({
            countryCode: catalogueCountry(property.countryCode, baseUserData?.country),
            locale,
            propertyId: property.id,
          }),
        })
        .map(([, data]) => data)
        .find((data): data is ResolvedCatalogue => Boolean(data?.shellProducts?.length))

      if (cachedCatalogue) {
        const validShellIds = new Set(
          cachedCatalogue.shellProducts.map((sp) => sp.id)
        )
        const staleIds = items
          .filter((item) => !validShellIds.has(item.shellProductId))
          .map((item) => item.id)

        if (staleIds.length > 0) {
          usePfStore.getState().clearStaleItems(staleIds)
          spawnWarningToast(
            t('purchase_flow_revamp:drafts.resume_stale_items', { count: staleIds.length })
          )
        }
      }
    }

    // 4. Orphaned staging photos (W-01). The drop already happened inside the
    //    hydration path (`useLoadDraftIntoStore`); this only surfaces the count
    //    so the user knows to re-upload instead of ordering an empty staging
    //    line unknowingly. Same mechanics as the stale-item toast above.
    //    Expired uploads get one toast of their own, which also covers the staging rooms they took along.
    if (result.droppedUploads > 0) {
      spawnWarningToast(
        t('purchase_flow_revamp:drafts.resume_uploads_dropped', { count: result.droppedUploads })
      )
    } else if (result.droppedStagingRooms > 0) {
      spawnWarningToast(
        t('purchase_flow_revamp:drafts.resume_staging_photos_dropped', { count: result.droppedStagingRooms })
      )
    }

    // ── Analytics + navigation ───────────────────────────────────────────────
    const ageSeconds = draft.updatedAt
      ? Math.max(0, Math.round((Date.now() - draft.updatedAt) / 1000))
      : -1
    const resumePath = resolveResumePath(resolveEntryPath({ hasProperty: !!draft.propertyAddress }), result.lastStep)
    trackAnalytics({
      type: PfRevampEventName.DRAFT_RESUMED,
      payload: { draftId: draft.draftId, ageSeconds, lastStep: result.lastStep, landedOn: resumePath },
    })
    return resumePath
  }, [loadDraftIntoStore, api, deleteDraft, spawnErrorToast, spawnWarningToast, queryClient, resolveEntryPath, revalidateAdminTarget, trackAnalytics, t, i18n.language, baseUserData?.country])
}
